import {
  UndefinedInitialDataOptions,
  queryOptions,
  useQuery,
} from '@tanstack/react-query';
import dayjs from 'dayjs';

import { OptimizationWorkloadDetailsResponse } from '@cast/types';

import { apiClient } from '@cast/infrastructure-api';
import { WorkloadQueryRangeSelection } from 'features/cluster/workload-optimization/types';

const WOOP_WORKLOAD_QUERY_KEY = 'woop-workload';

export const getWorkloadQueryKey = (
  params: {
    clusterId?: string;
    workloadId?: string;
    includeMetrics?: boolean;
    key?: string;
  } = {}
) => {
  if (params.clusterId && params.workloadId) {
    return [
      WOOP_WORKLOAD_QUERY_KEY,
      params.clusterId,
      params.workloadId,
      params.includeMetrics ?? false,
      params.key,
    ] as const;
  } else if (params.clusterId) {
    return [WOOP_WORKLOAD_QUERY_KEY, params.clusterId] as const;
  }
  return [WOOP_WORKLOAD_QUERY_KEY] as const;
};

export type WorkloadQueryData = OptimizationWorkloadDetailsResponse & {
  loadedLength?: number;
};

export type UseWorkloadQueryArgs = {
  clusterId: string;
  workloadId: string;
  includeMetrics?: boolean;
  includeCosts?: boolean;
  selection: WorkloadQueryRangeSelection;
  key: string;
} & Omit<
  UndefinedInitialDataOptions<WorkloadQueryData>,
  'queryFn' | 'queryKey'
>;

export const getWorkloadQueryOptions = ({
  clusterId,
  workloadId,
  includeMetrics,
  includeCosts,
  selection,
  key,
  ...rest
}: UseWorkloadQueryArgs) => {
  const length = dayjs(selection.range?.to).diff(
    selection.range?.from,
    'milliseconds'
  );

  return queryOptions<WorkloadQueryData>({
    refetchInterval: () => {
      if (!length || !selection.periodLabel) {
        return false;
      }
      const duration = dayjs.duration(length);
      if (duration.asMinutes() <= 15) {
        return dayjs.duration(1, 'minute').asMilliseconds();
      }
      if (duration.asHours() <= 1) {
        return dayjs.duration(5, 'minutes').asMilliseconds();
      }
      if (duration.asHours() <= 24) {
        return dayjs.duration(30, 'minutes').asMilliseconds();
      }
      if (duration.asHours() <= 7 * 24) {
        return dayjs.duration(1, 'hour').asMilliseconds();
      }
      return false;
    },
    ...rest,
    queryKey: getWorkloadQueryKey({
      clusterId,
      workloadId,
      includeMetrics,
      key,
    }),
    queryFn: async () => {
      const fromTime = selection.range?.from;
      const toTime = selection.range?.to;
      const { data } = await apiClient.workloadOptimization.getWorkload({
        clusterId,
        workloadId,
        includeMetrics,
        includeCosts,
        fromTime,
        toTime,
      });
      return { ...(data || {}), loadedLength: length };
    },
  });
};

export const useWorkloadQuery = (args: UseWorkloadQueryArgs) => {
  const { isLoading, isFetching, data, error, refetch, isPlaceholderData } =
    useQuery(getWorkloadQueryOptions(args));

  return { isLoading, isFetching, error, refetch, isPlaceholderData, data };
};
